/**
 * Lead Dossier - The Gameplan
 * Full intelligence view for a single lead: timeline, gameplan, BANT, signals.
 */

import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useLeadDossier } from '@/hooks/useLeadDossier';
import { useGenerateDraft } from '@/hooks/useLinkedInDrafts';
import { describeGateBlock } from '@/lib/crm/reply-gate-copy';
import { useToast } from '@/hooks/use-toast';
import {
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGE_COLORS,
  BANT_COLORS,
  DISC_COLORS,
  MOMENTUM_COLORS,
  buildDealNarrative,
  computeTacitScore,
  type FunnelStage,
  type BANTStatus,
} from '@/lib/intelligence-helpers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ArrowLeft,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  Inbox,
  MessageSquare,
  Send,
  Clock,
  XCircle,
  CheckCircle2,
  Lightbulb,
  Target,
  User,
  Building2,
  Trash2,
  Sparkles,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRef, useState } from 'react';
import { isLiveDraft } from '@/lib/crm/live-draft';
import type { TimelineEntry } from '@/hooks/useLeadDossier';
import {
  formatUiDate,
  formatUiDateTime,
  formatUiPercent,
} from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';

// ─────────────────────────────────────────────────────────────────────────
// Funnel Stage Indicator
// ─────────────────────────────────────────────────────────────────────────

const FUNNEL_STAGES_ORDERED: FunnelStage[] = [
  'qualifying', 'needs_followup', 'next_step_defined', 'meeting_booked',
];

function FunnelStageIndicator({ current }: { current: FunnelStage }) {
  const isTerminal = current === 'graceful_exit' || current === 'blocked';

  if (isTerminal) {
    return (
      <Badge variant="outline" className={cn('text-xs', FUNNEL_STAGE_COLORS[current])}>
        {FUNNEL_STAGE_LABELS[current]}
      </Badge>
    );
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {FUNNEL_STAGES_ORDERED.map((stage, idx) => {
        const currentIdx = FUNNEL_STAGES_ORDERED.indexOf(current);
        const isActive = idx === currentIdx;
        const isPast = idx < currentIdx;

        return (
          <div key={stage} className="flex min-w-0 items-center gap-1">
            <div
              className={cn(
                'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-all',
                isActive
                  ? FUNNEL_STAGE_COLORS[stage]
                  : isPast
                    ? 'bg-green-500/10 text-green-400/60'
                    : 'bg-muted text-muted-foreground/50'
              )}
            >
              {FUNNEL_STAGE_LABELS[stage]}
            </div>
            {idx < FUNNEL_STAGES_ORDERED.length - 1 && (
              <div className={cn('h-px w-3', isPast || isActive ? 'bg-green-500/40' : 'bg-border')} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BANT Tracker
// ─────────────────────────────────────────────────────────────────────────

function BANTTracker({ bant }: { bant: { budget: BANTStatus; authority: BANTStatus; need: BANTStatus; timeline: BANTStatus } }) {
  const dimensions = ['budget', 'authority', 'need', 'timeline'] as const;
  const labels = { budget: 'B', authority: 'A', need: 'N', timeline: 'T' };
  const fullLabels = { budget: 'Budget', authority: 'Authority', need: 'Need', timeline: 'Timeline' };

  return (
    <div className="flex items-center gap-2">
      {dimensions.map((dim) => {
        const status = bant[dim] || 'unknown';
        return (
          <div key={dim} className="flex flex-col items-center gap-1" title={`${fullLabels[dim]}: ${status}`}>
            <div className={cn('h-3 w-3 rounded-full', BANT_COLORS[status])} />
            <span className="text-[10px] text-muted-foreground">{labels[dim]}</span>
          </div>
        );
      })}
      <span className="ml-1 text-xs text-muted-foreground">
        {Object.values(bant).filter((v) => v === 'qualified').length}/4
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Conversation Timeline
// ─────────────────────────────────────────────────────────────────────────

function TimelineBubble({ entry, isGenerating }: { entry: TimelineEntry; isGenerating: boolean }): JSX.Element {
  const sentMessage = entry.editedResponse || entry.draftResponse;
  const isSent = entry.status === 'sent';
  // The SAME predicate the queue enforces (src/lib/crm/live-draft.ts), not a
  // hand-rolled status/superseded check — that independently drifted once
  // already by omitting the non-empty-message rule (F4, round 8).
  const isLive = isLiveDraft({ status: entry.status, is_superseded: entry.isSuperseded, incoming_message: entry.incomingMessage });
  const isPending = isLive && entry.status === 'pending_approval';
  const isAwaitingDraft = isLive && entry.status === 'awaiting_draft';
  const isRejected = entry.status === 'rejected';
  const [showRejected, setShowRejected] = useState(false);

  let responseSection: JSX.Element;
  if (isRejected) {
    responseSection = (
      <div className="flex justify-end">
        <button
          type="button"
          className="min-h-11 max-w-[80%] rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setShowRejected(!showRejected)}
          aria-expanded={showRejected}
          aria-controls={`rejected-draft-${entry.id}`}
        >
          <span className="flex items-center gap-1">
            <XCircle className="h-3 w-3" aria-hidden="true" />
            Draft rejected
            {showRejected && (
              <span
                id={`rejected-draft-${entry.id}`}
                className="ml-1 line-through"
                data-content-origin="generated"
              >
                {sentMessage}
              </span>
            )}
          </span>
        </button>
      </div>
    );
  } else if (isAwaitingDraft) {
    // F3 (round 14): truthful idle state — the webhook inserts this row with
    // NO AI call (api/os/webhooks/heyreach.ts:930-955); generation is
    // strictly on-demand via the page's own CTA. "Generating…" only while
    // `useGenerateDraft` is ACTUALLY pending for THIS draft id — never a
    // standing claim that work is already underway.
    responseSection = (
      <div className="flex justify-end">
        <div className="max-w-[80%] space-y-1">
          <div className="rounded-lg rounded-tr-sm border border-dashed border-muted-foreground/40 bg-muted/20 px-4 py-3">
            <p className="text-sm italic text-muted-foreground">
              {isGenerating ? 'Generating…' : 'Draft not generated yet'}
            </p>
          </div>
          <p className="flex items-center justify-end gap-0.5 text-[10px] text-muted-foreground">
            <Clock className="h-2.5 w-2.5" aria-hidden="true" />
            {isGenerating ? 'Generation in progress' : 'Generation required'}
          </p>
        </div>
      </div>
    );
  } else {
    responseSection = (
      <div className="flex justify-end">
        <div className="max-w-[80%] space-y-1">
          <div
            className={cn(
              'rounded-lg rounded-tr-sm px-4 py-3',
              isPending
                ? 'border border-dashed border-amber-500/40 bg-amber-500/5'
                : 'bg-primary/15'
            )}
          >
            <p
              className={cn('text-sm', isPending ? 'text-amber-200' : 'text-foreground')}
              data-content-origin="generated"
            >
              {sentMessage}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Badge variant="outline" className="text-[10px] py-0 h-4">
              {entry.methodology}
            </Badge>
            {isSent && entry.sentAt && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <Send className="h-2.5 w-2.5" />
                {formatUiDateTime(entry.sentAt)}
              </p>
            )}
            {isPending && (
              <p className="text-[10px] text-amber-400 flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" />
                Awaiting approval
              </p>
            )}
            {entry.status === 'approved' && !isSent && (
              <p className="text-[10px] text-green-400 flex items-center gap-0.5">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Approved
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Their message (left) */}
      <div className="flex justify-start">
        <div className="max-w-[80%] space-y-1">
          <div className="rounded-lg rounded-tl-sm bg-muted px-4 py-3">
            <p
              className="text-sm text-foreground"
              data-content-origin="external"
            >
              {entry.incomingMessage}
            </p>
          </div>
          <p className="text-[10px] text-muted-foreground">
            {formatUiDateTime(entry.incomingAt)}
          </p>
        </div>
      </div>

      {/* Our response (right) */}
      {responseSection}
    </div>
  );
}

/** Mirrors `leadDossierHref` (src/components/inbox/reply-queue-panel.tsx) for
 * the reverse Dossier→Inbox leg of the same identity carry: the exact live
 * draft id must survive alongside the thread id, or the inbox's own winner
 * selection (`getLiveDraftWinner`, src/hooks/useThreadMessages.ts) can
 * resurface a DIFFERENT sender's live draft on the same thread — multiple
 * senders' live drafts on one thread is a legal schema state, not an
 * edge case to ignore. */
function inboxHrefForLiveDraft(threadId: string, draftId: string): string {
  const search = new URLSearchParams({ thread: threadId, draft: draftId });
  return `/inbox?${search.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────

export default function LeadDossier(): JSX.Element {
  const { heyreachLeadId } = useParams<{ heyreachLeadId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const carriedDraftId = searchParams.get('draft');
  const carriedThreadId = searchParams.get('thread');
  const carriedDraft = carriedDraftId
    ? { draftId: carriedDraftId, threadId: carriedThreadId || null }
    : null;
  const { data: dossier, isLoading, error, refetch } = useLeadDossier(heyreachLeadId, carriedDraft);
  const { toast } = useToast();
  // F1(b): a null-thread `awaiting_draft` has no Inbox thread view to
  // generate from — the on-demand mutation only needs a draftId
  // (api/os/linkedin/generate-draft.ts), so this triggers it directly
  // rather than inventing a new endpoint or leaving a false "being
  // generated" badge in place.
  const generateDraftMutation = useGenerateDraft();
  const generateInFlightRef = useRef(false);
  const handleGenerateDraft = (draftId: string): void => {
    if (generateInFlightRef.current || generateDraftMutation.isPending) return;
    generateInFlightRef.current = true;
    generateDraftMutation.mutate(
      { draftId },
      {
        onSuccess: (data) => {
          if (!data.blocked) return;
          const { title, description } = describeGateBlock(data.rule);
          toast({ title, description });
        },
        onError: (mutationError) => {
          toast({
            title: 'Draft generation failed',
            description: toSafeUiError(mutationError).description,
            variant: 'destructive',
          });
        },
        onSettled: () => {
          generateInFlightRef.current = false;
        },
      }
    );
  };

  useDocumentTitle(
    dossier
      ? `${dossier.profile.leadName} — Lead Dossier | Tacit`
      : 'Lead Dossier | Tacit'
  );

  if (isLoading) {
    return (
      <Layout>
        <main
          className="mx-auto max-w-7xl space-y-6 p-6"
          role="status"
          aria-label="Loading lead dossier"
          aria-live="polite"
          aria-busy="true"
        >
          <h1 className="text-2xl font-bold">Lead dossier</h1>
          <span className="sr-only">Loading lead dossier</span>
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-4">
              <Skeleton className="h-64 rounded-lg" />
              <Skeleton className="h-80 rounded-lg" />
            </div>
            <div className="lg:col-span-2">
              <Skeleton className="h-[600px] rounded-lg" />
            </div>
          </div>
        </main>
      </Layout>
    );
  }

  if (error) {
    const safeError = toSafeUiError(error);
    if (safeError.kind === 'not_found') {
      return (
        <Layout>
          <main className="flex min-h-[50vh] items-center justify-center px-4 py-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold">Lead not found</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This lead may have been removed or is not available to you.
              </p>
              <Button
                variant="outline"
                className="mt-4 min-h-11 md:min-h-11"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Go back
              </Button>
            </div>
          </main>
        </Layout>
      );
    }

    return (
      <Layout>
        <main className="mx-auto flex min-h-[50vh] max-w-xl items-center px-4 py-8">
          <div role="alert" className="w-full rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center">
            <h1 className="text-2xl font-bold">Lead dossier unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {safeError.description} {safeError.recovery}
            </p>
            {safeError.retryable && (
              <Button
                variant="outline"
                className="mt-4 min-h-11 md:min-h-11"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            )}
          </div>
        </main>
      </Layout>
    );
  }

  if (!dossier) {
    return (
      <Layout>
        <main className="flex min-h-[50vh] items-center justify-center px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Lead dossier is empty</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              No conversation history has been recorded for this lead yet.
            </p>
            <Button
              variant="outline"
              className="mt-4 min-h-11 md:min-h-11"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Go back
            </Button>
          </div>
        </main>
      </Layout>
    );
  }

  const { profile, campaignName, gameplan, disc, enrichment, signals, timeline, approachesUsed, livePendingDraft, didFallbackFromCarriedDraft } = dossier;
  const tacitScore = computeTacitScore(null, gameplan.confidence, gameplan.momentum);
  const narrative = buildDealNarrative(
    profile.leadName, profile.leadTitle, profile.leadCompany,
    gameplan, disc, profile.exchangeCount,
  );

  // F2: a live draft whose thread is Trashed must not offer a navigable
  // "Review Draft" — useThreadById would deterministically dead-end at
  // "Conversation not found" (os_conversation_inbox excludes soft-deleted
  // threads). F1: generation is strictly on-demand — the webhook inserts
  // `awaiting_draft` rows with NO AI call (api/os/webhooks/heyreach.ts:
  // 930-955) — so every remaining branch must be truthful about that AND
  // either act or honestly explain why it can't. if/else (not a nested
  // ternary) so the branches stay independently readable.
  let reviewDraftAction: JSX.Element | null = null;
  if (livePendingDraft?.isThreadDeleted) {
    reviewDraftAction = (
      <Badge variant="outline" className="min-h-11 gap-1 px-3 text-xs">
        <Trash2 className="h-3 w-3" aria-hidden="true" />
        Conversation is in Trash — restore it to review
      </Badge>
    );
  } else if (livePendingDraft?.status === 'pending_approval' && livePendingDraft.threadId) {
    // Narrowed to locals: TS control-flow narrowing on `livePendingDraft.*`
    // does not survive into the onClick closure below.
    const reviewThreadId = livePendingDraft.threadId;
    const reviewDraftId = livePendingDraft.id;
    reviewDraftAction = (
      <Button
        size="sm"
        className="min-h-11 md:min-h-11"
        onClick={() => navigate(inboxHrefForLiveDraft(reviewThreadId, reviewDraftId))}
      >
        <Inbox className="mr-1 h-3.5 w-3.5" /> Review Draft
      </Button>
    );
  } else if (livePendingDraft?.status === 'awaiting_draft' && livePendingDraft.threadId) {
    // F1(a): the on-demand generation UI lives in Inbox (its
    // "Generate Draft" button, src/pages/inbox.tsx) — send the operator
    // there instead of duplicating that flow, honestly labeled as a
    // navigation, not a claim that generation is already underway.
    const generateThreadId = livePendingDraft.threadId;
    const generateDraftId = livePendingDraft.id;
    reviewDraftAction = (
      <Button
        size="sm"
        variant="outline"
        className="min-h-11 md:min-h-11"
        onClick={() => navigate(inboxHrefForLiveDraft(generateThreadId, generateDraftId))}
      >
        <Inbox className="mr-1 h-3.5 w-3.5" /> Generate draft in Inbox
      </Button>
    );
  } else if (livePendingDraft?.status === 'awaiting_draft') {
    // F1(b): threadId === null — no Inbox thread view exists to generate
    // from, so trigger the same draftId-only mutation directly.
    const draftId = livePendingDraft.id;
    reviewDraftAction = (
      <Button
        size="sm"
        className="min-h-11 gap-1.5 md:min-h-11"
        disabled={generateDraftMutation.isPending}
        aria-busy={generateDraftMutation.isPending}
        onClick={() => handleGenerateDraft(draftId)}
      >
        {generateDraftMutation.isPending ? (
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {generateDraftMutation.isPending ? 'Generating…' : 'Generate Draft'}
      </Button>
    );
  } else if (livePendingDraft) {
    // F1(c): pending_approval with threadId === null. Inbox review is
    // thread-scoped (useThreadMessages requires a thread id); no other
    // surface accepts a bare draft id — an honest explanation of why there
    // is nothing to click, never a broken link or a false "Awaiting approval".
    reviewDraftAction = (
      <Badge variant="outline" className="min-h-11 gap-1 px-3 text-xs">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        Draft ready — thread link broken, can't open for review
      </Badge>
    );
  }

  return (
    <Layout>
      <main className="mx-auto w-full min-w-0 max-w-7xl space-y-6 overflow-x-clip px-4 py-6 sm:px-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1
                className="text-2xl font-bold"
                data-content-origin="external"
              >
                {profile.leadName}
              </h1>
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {profile.leadTitle && (
                  <span data-content-origin="external">{profile.leadTitle}</span>
                )}
                {profile.leadTitle && profile.leadCompany && <span>at</span>}
                {profile.leadCompany && (
                  <span className="font-medium" data-content-origin="external">
                    {profile.leadCompany}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {profile.leadLinkedinUrl && (
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 md:min-h-11"
                asChild
              >
                <a href={profile.leadLinkedinUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-1 h-3.5 w-3.5" /> LinkedIn
                </a>
              </Button>
            )}
            {reviewDraftAction}
          </div>
        </div>

        {didFallbackFromCarriedDraft && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-status-warning/40 bg-status-warning/10 p-3 text-sm"
          >
            <span>
              {livePendingDraft
                ? 'This Reply Queue item changed. The latest live draft for this lead is shown instead.'
                : 'This Reply Queue item is no longer live and no other live draft exists for this lead.'}
            </span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Refresh dossier
            </Button>
          </div>
        )}

        {/* Deal Narrative Banner */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1 space-y-2">
                <p
                  className="break-words text-sm leading-relaxed text-foreground"
                  data-content-origin="generated"
                >
                  {narrative}
                </p>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <FunnelStageIndicator current={gameplan.funnelStage} />
                  <Badge variant="outline" className="text-xs">
                    Tacit Score: {tacitScore}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Two-column layout */}
        <div className="grid min-w-0 gap-6 lg:grid-cols-3">
          {/* Left column: Profile + Gameplan + Signals */}
          <div className="min-w-0 space-y-4">
            {/* Profile Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Lead Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {disc && (
                    <Badge variant="outline" className={cn('text-xs', DISC_COLORS[disc.primary])}>
                      DISC: {disc.primary}{disc.secondary ? `/${disc.secondary}` : ''}
                    </Badge>
                  )}
                  {enrichment.industry && (
                    <Badge variant="outline" className="text-xs">{enrichment.industry}</Badge>
                  )}
                  {enrichment.companySize && (
                    <Badge variant="outline" className="text-xs">{enrichment.companySize}</Badge>
                  )}
                  {enrichment.seniority && (
                    <Badge variant="outline" className="text-xs">{enrichment.seniority}</Badge>
                  )}
                  {enrichment.decisionMaker && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                      Decision Maker
                    </Badge>
                  )}
                </div>

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    <span>{profile.leadTitle || 'Unknown title'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5" />
                    <span>{profile.leadCompany || 'Unknown company'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Target className="h-3.5 w-3.5" />
                    {/* DIFF r1 F4: an absent stats row is NOT a nameless campaign. `os_campaign_stats`
                        is grouped from the same drafts this dossier just read, and
                        `campaign_display_name` COALESCEs to a non-null pseudo-name — so zero rows
                        after a draft resolved is a completeness/RLS/snapshot failure. Rendering the
                        generic "View Campaign" reported that failure as ordinary success. The link
                        still works (the id is authoritative on the draft row), so this degrades
                        locally and stays usable rather than failing the whole dossier. */}
                    <Link
                      to={`/campaigns/${profile.heyreachCampaignId}`}
                      className="inline-flex min-h-11 items-center underline-offset-4 hover:text-foreground hover:underline"
                      data-content-origin={campaignName ? 'domain' : undefined}
                      data-testid={campaignName ? undefined : 'dossier-campaign-unresolved'}
                    >
                      {campaignName || 'Campaign name unresolved'}
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MessageSquare className="h-3.5 w-3.5" />
                    <span>{profile.exchangeCount} exchanges</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    <span>First contact: {formatUiDate(profile.firstContact)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gameplan Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Gameplan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Funnel Stage */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Funnel Stage</p>
                  <FunnelStageIndicator current={gameplan.funnelStage} />
                </div>

                {/* BANT */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">BANT Qualification</p>
                  <BANTTracker bant={gameplan.bant} />
                </div>

                {/* Momentum */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Momentum</p>
                  <div className="flex items-center gap-2">
                    {gameplan.momentum.trend === 'heating' && <TrendingUp className="h-4 w-4 text-green-400" />}
                    {gameplan.momentum.trend === 'cooling' && <TrendingDown className="h-4 w-4 text-amber-400" />}
                    {gameplan.momentum.trend === 'stable' && <Minus className="h-4 w-4 text-muted-foreground" />}
                    <span className={cn('text-sm font-medium', MOMENTUM_COLORS[gameplan.momentum.trend])}>
                      {formatUiPercent(gameplan.momentum.score)}
                      <span className="ml-1 font-normal capitalize">{gameplan.momentum.trend}</span>
                    </span>
                  </div>
                  {gameplan.momentum.signals.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {gameplan.momentum.signals.slice(0, 3).map((signal) => (
                        <Badge key={signal} variant="outline" className="text-[10px] text-muted-foreground">
                          {signal}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Methodology */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Methodology</p>
                  <p className="text-sm">
                    <span className="font-medium capitalize">{gameplan.methodology}</span>
                    {gameplan.confidence > 0 && (
                      <span className="text-muted-foreground"> ({Math.round(gameplan.confidence * 100)}% confidence)</span>
                    )}
                  </p>
                  {gameplan.methodologyReasoning && (
                    <p className="mt-1 text-xs text-muted-foreground">{gameplan.methodologyReasoning}</p>
                  )}
                </div>

                <Separator />

                {/* Next Step */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Recommended Next Step</p>
                  <p className="text-sm text-foreground">{gameplan.nextStep}</p>
                </div>
              </CardContent>
            </Card>

            {/* Approaches Tried */}
            <Collapsible>
              <Card>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="flex-row items-center justify-between pb-3">
                    <CardTitle className="text-base">Approaches Tried</CardTitle>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-2 pt-0">
                    {approachesUsed.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No approaches have been recorded yet.
                      </p>
                    ) : (
                      approachesUsed.map((a) => (
                        <div key={a.methodology} className="flex items-center justify-between rounded-lg border border-border p-2.5">
                          <span className="text-sm font-medium capitalize">{a.methodology}</span>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{a.count} message{a.count !== 1 ? 's' : ''}</span>
                            {a.responseCount > 0 && (
                              <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/30">
                                {a.responseCount} response{a.responseCount !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Conversation Signals */}
            {signals.length > 0 && (
              <Collapsible>
                <Card>
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="flex-row items-center justify-between pb-3">
                      <CardTitle className="text-base">Detected Signals</CardTitle>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="space-y-2 pt-0">
                      {signals.map((s, i) => (
                        <div key={`${s.signal}-${i}`} className="rounded-lg border border-border p-2.5">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px] uppercase">{s.signal}</Badge>
                            <span className="text-[10px] text-muted-foreground">Message #{s.messageIndex}</span>
                          </div>
                          {s.evidence && (
                            <p className="mt-1 text-xs text-muted-foreground italic">"{s.evidence}"</p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}
          </div>

          {/* Right column: Conversation Timeline */}
          <div className="min-w-0 lg:col-span-2">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Conversation Timeline</CardTitle>
                  <span className="text-xs text-muted-foreground">{timeline.length} exchanges</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {timeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No conversation activity has been recorded yet.
                  </p>
                ) : (
                  timeline.map((entry) => (
                    <TimelineBubble
                      key={entry.id}
                      entry={entry}
                      isGenerating={generateDraftMutation.isPending && generateDraftMutation.variables?.draftId === entry.id}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </Layout>
  );
}
